// ElevenLabs Text-to-Speech Service
// Provides consistent, high-quality voice for rehabilitation encouragement

class ElevenLabsTTSService {
  constructor() {
    // API key from environment variable
    this.apiKey = process.env.REACT_APP_ELEVENLABS_API_KEY;
    this.baseURL = 'https://api.elevenlabs.io/v1/text-to-speech';
    
    // Validate API key on initialization
    if (!this.apiKey) {
      console.warn('⚠️ ElevenLabs API key not found in environment variables.');
      console.warn('💡 Please set REACT_APP_ELEVENLABS_API_KEY in your .env file');
      console.warn('🔗 Get your API key from: https://elevenlabs.io/app/settings/api-keys');
    } else {
      console.log('✅ ElevenLabs API key loaded from environment');
    }
    
    // Voice IDs for different female voices
    // We'll test these to find the warmest one for rehab encouragement
    this.voiceIds = {
      rachel: '21m00Tcm4TlvDq8ikWAM',     // Rachel - calm
      domi: 'AZnzlk1XvdvUeBnXmlld',       // Domi - young
      bella: 'EXAVITQu4vr4xnSDxMaL',      // Bella - soft American
      elli: 'MF3mGyEYCl7XYWbV9V6O',       // Elli - young American
      charlotte: 'XB0fDUnXU5powFXDhCwa',   // Charlotte - seductive
      sarah: 'EXAVITQu4vr4xnSDxMaL',      // Sarah - soft
      nicole: 'piTKgcLEGmPE4e6mEKli',     // Nicole - whispery
      jessie: 't0jbNlBVZ17f02VDIeMI',     // Jessie - raspy
      laura: 'FGY2WhTYpPnrIDTdsKH5',      // Laura - upbeat American
      grace: 'oWAxZDx7w5VEj9dCyTzz',      // Grace - Southern US
    };
    
    // Default to Bella (soft American) as it sounds warm and encouraging
    this.currentVoiceId = this.voiceIds.bella;
    this.currentVoiceName = 'bella';
    
    // Configuration
    this.isEnabled = true;
    this.modelId = 'eleven_monolingual_v1'; // Best for English
    
    // Voice settings for warm, encouraging tone
    this.voiceSettings = {
      stability: 0.85,        // Higher stability for calmer delivery
      similarity_boost: 0.75, // Good voice consistency
      style: 0.2,            // Less expressiveness for calmer tone
      use_speaker_boost: true
    };
    
    // Milestone tracking (same as HUME)
    this.spokenMilestones = new Map();
    this.lastSpeakTime = 0;
    this.cooldownMs = 2000;
    this.encouragementMilestones = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    
    // Encouragement messages - longer and more supportive
    this.messages = {
      10: [
        "Ten repetitions completed! You're doing amazing. Remember to breathe and take breaks if you need them. Keep up the fantastic work!",
        "Wonderful! You've reached ten. Your consistency is paying off. Stay focused on your form and you'll reach your goals!",
        "Ten down! You're doing brilliantly. Take a moment to appreciate your progress. Every rep counts!"
      ],
      20: [
        "Twenty repetitions! You're in the home stretch now. Your perseverance is truly admirable. Keep going at your own pace!",
        "Fantastic achievement - twenty done! You're showing incredible strength and determination. Just a few more to go!",
        "Twenty completed! You should be so proud of yourself. Take deep breaths and finish strong. You're almost there!"
      ],
      30: [
        "Thirty repetitions! You've done it! This is a wonderful achievement. Take a moment to celebrate your success. You should be very proud!",
        "Absolutely fantastic! You've completed all thirty repetitions. Your dedication to your rehabilitation is truly remarkable. Well done!",
        "Congratulations! Thirty repetitions completed. You've shown incredible determination and strength. This is a significant milestone in your recovery!"
      ],
      40: [
        "Forty repetitions! Outstanding endurance! You're pushing beyond limits and showing remarkable resilience. Keep breathing steadily!",
        "Incredible! Forty completed. Your strength is growing with every session. Remember to maintain proper form as you continue!",
        "Forty down! You're demonstrating exceptional commitment. Take a moment if you need it - you're doing phenomenally well!"
      ],
      50: [
        "Fifty repetitions! You're halfway to one hundred! This is extraordinary progress. Your dedication is truly inspiring!",
        "Amazing milestone - fifty done! You're building incredible stamina. Keep listening to your body and maintain that excellent pace!",
        "Fifty completed! You've reached an impressive halfway point. Your perseverance is remarkable. Keep up this fantastic momentum!"
      ],
      60: [
        "Sixty repetitions! You're showing champion-level endurance. Remember to breathe deeply and stay focused on your form!",
        "Fantastic! Sixty done. Your consistency and determination are paying off beautifully. You're stronger than you know!",
        "Sixty completed! You're demonstrating incredible mental and physical strength. Keep going at your own comfortable pace!"
      ],
      70: [
        "Seventy repetitions! You're in elite territory now. Your commitment to improvement is absolutely remarkable. Keep it up!",
        "Incredible achievement - seventy done! You're showing extraordinary dedication. Remember, every rep is building your strength!",
        "Seventy completed! You're proving that persistence pays off. Take deep breaths and continue when you're ready!"
      ],
      80: [
        "Eighty repetitions! You're showing legendary endurance. Your progress is nothing short of amazing. Just twenty more to go!",
        "Outstanding! Eighty done. You're demonstrating the power of determination. Keep breathing steadily and maintain your rhythm!",
        "Eighty completed! You're so close to one hundred. Your strength and willpower are truly inspiring. Keep pushing forward!"
      ],
      90: [
        "Ninety repetitions! You're in the final stretch! Your achievement today is absolutely extraordinary. Just ten more!",
        "Phenomenal! Ninety done. You're showing what true dedication looks like. Take your time with these last ten repetitions!",
        "Ninety completed! You're almost at one hundred. This is an incredible display of strength and perseverance. You've got this!"
      ],
      100: [
        "One hundred repetitions! Absolutely incredible! You've achieved something truly special today. This is a monumental accomplishment!",
        "Congratulations! One hundred completed! You've shown extraordinary strength, dedication, and perseverance. You should be incredibly proud!",
        "Amazing! You've reached one hundred repetitions! This is a testament to your hard work and commitment. What an outstanding achievement!"
      ]
    };
    
    // Audio queue to prevent overlapping
    this.audioQueue = [];
    this.currentAudio = null; // Track currently playing audio for cleanup
    this.isPlaying = false;
    this.isSpeaking = false;
    this.currentText = '';
    this.onSpeakingStart = null;
    this.onSpeakingEnd = null;
    
    // Inactivity detection
    this.lastActivityTime = Date.now();
    this.inactivityCheckInterval = null;
    this.inactivityThreshold = 50000; // 50 seconds
    this.hasAskedIfOkay = false;
    
    // Inactivity messages
    this.inactivityMessages = [
      "Hey there, I noticed you've paused. Are you doing okay? Take your time, there's no rush.",
      "Just checking in - are you alright? Remember, it's perfectly fine to take a break whenever you need.",
      "I see you're taking a moment. That's great! Rest is important. Let me know when you're ready to continue."
    ];
    
    // Add visibility change listener to stop monitoring when page is hidden
    // This prevents issues in embedded browsers like Equiva app
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          console.log('📱 Page hidden, stopping inactivity monitoring');
          this.stopInactivityMonitoring();
          this.stopAllAudio();
        }
      });
      
      // Also listen for beforeunload to cleanup when navigating away
      window.addEventListener('beforeunload', () => {
        console.log('📤 Page unloading, cleanup TTS');
        this.stopInactivityMonitoring();
        this.stopAllAudio();
      });
      
      // Listen for pagehide event (iOS Safari compatibility)
      window.addEventListener('pagehide', () => {
        console.log('📤 Page hiding, cleanup TTS');
        this.stopInactivityMonitoring();
        this.stopAllAudio();
      });
    }
  }

  // Enable/disable the service
  setEnabled(enabled) {
    this.isEnabled = enabled;
    console.log(`🎤 ElevenLabs TTS ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Set callbacks for animation control
  setAnimationCallbacks(onStart, onEnd) {
    this.onSpeakingStart = onStart;
    this.onSpeakingEnd = onEnd;
  }

  // Start speaking (triggers animation)
  startSpeaking(text) {
    this.isSpeaking = true;
    this.currentText = text;
    if (this.onSpeakingStart) {
      this.onSpeakingStart(text);
    }
  }

  // Stop speaking (stops animation)
  stopSpeaking() {
    this.isSpeaking = false;
    this.currentText = '';
    if (this.onSpeakingEnd) {
      this.onSpeakingEnd();
    }
  }

  // Change voice
  setVoice(voiceName) {
    if (this.voiceIds[voiceName]) {
      this.currentVoiceId = this.voiceIds[voiceName];
      this.currentVoiceName = voiceName;
      console.log(`🎤 Voice changed to: ${voiceName}`);
      return true;
    } else {
      console.log('❌ Unknown voice. Available:', Object.keys(this.voiceIds).join(', '));
      return false;
    }
  }

  // Adjust voice settings
  setVoiceSettings(settings) {
    this.voiceSettings = { ...this.voiceSettings, ...settings };
    console.log('🎤 Voice settings updated:', this.voiceSettings);
  }

  // Main TTS function using ElevenLabs API
  async speakText(text) {
    if (!this.isEnabled) {
      return this.fallbackToBrowserTTS(text);
    }

    if (!this.apiKey) {
      console.error('❌ ElevenLabs API key not configured');
      return this.fallbackToBrowserTTS(text);
    }

    try {
      console.log(`🎤 Sending to ElevenLabs (${this.currentVoiceName}):`, text);
      
      // Start speaking animation
      this.startSpeaking(text);
      
      const response = await fetch(`${this.baseURL}/${this.currentVoiceId}`, {
        method: 'POST',
        headers: {
          'xi-api-key': this.apiKey,
          'Content-Type': 'application/json',
          'Accept': 'audio/mpeg'
        },
        body: JSON.stringify({
          text: text,
          model_id: this.modelId,
          voice_settings: this.voiceSettings
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ ElevenLabs API error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        return this.fallbackToBrowserTTS(text);
      }

      // Get audio data as blob
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Play the audio
      return await this.playAudio(audioUrl);
      
    } catch (error) {
      console.error('❌ ElevenLabs TTS error:', error);
      return this.fallbackToBrowserTTS(text);
    }
  }

  // Play audio with queue management
  async playAudio(audioUrl) {
    return new Promise((resolve) => {
      const audio = new Audio(audioUrl);
      audio.volume = 0.9;
      audio.playbackRate = 0.9; // Slow down voice for calmer delivery
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null; // Clear reference when audio ends
        this.isPlaying = false;
        this.stopSpeaking(); // Stop animation when audio ends
        this.processAudioQueue();
        resolve(true);
      };
      
      audio.onerror = (error) => {
        console.error('❌ Audio playback error:', error);
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null; // Clear reference on error
        this.isPlaying = false;
        this.stopSpeaking(); // Stop animation on error
        resolve(false);
      };
      
      // Add to queue if something is playing
      if (this.isPlaying) {
        this.audioQueue.push(audio);
      } else {
        this.currentAudio = audio; // Track currently playing audio
        this.isPlaying = true;
        audio.play()
          .then(() => console.log('✅ ElevenLabs TTS playing'))
          .catch(err => {
            console.error('❌ Play error:', err);
            this.currentAudio = null;
            this.isPlaying = false;
            resolve(false);
          });
      }
    });
  }

  // Process audio queue
  processAudioQueue() {
    if (this.audioQueue.length > 0 && !this.isPlaying) {
      const nextAudio = this.audioQueue.shift();
      this.isPlaying = true;
      nextAudio.play()
        .then(() => console.log('✅ Playing queued audio'))
        .catch(err => {
          console.error('❌ Queue play error:', err);
          this.isPlaying = false;
          this.processAudioQueue();
        });
    }
  }

  // Fallback to browser TTS
  fallbackToBrowserTTS(text) {
    try {
      console.log('🔊 Using browser TTS fallback');
      
      // Start speaking animation
      this.startSpeaking(text);
      
      const utterance = new SpeechSynthesisUtterance(text);
      
      // Add event listeners for animation control
      utterance.onend = () => {
        this.stopSpeaking(); // Stop animation when speech ends
      };
      
      utterance.onerror = () => {
        this.stopSpeaking(); // Stop animation on error
      };
      
      // Try to find a consistent female voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.name.includes('Samantha') || // macOS
        voice.name.includes('Microsoft Zira') || // Windows
        voice.name.includes('Google US English Female') || // Chrome
        (voice.lang === 'en-US' && voice.name.includes('Female'))
      ) || voices.find(voice => voice.lang.startsWith('en'));
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      utterance.rate = 1.0;
      utterance.pitch = 1.1; // Slightly higher for warmth
      utterance.volume = 0.9;
      
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
      return true;
    } catch (error) {
      console.error('❌ Browser TTS error:', error);
      this.stopSpeaking(); // Stop animation on error
      return false;
    }
  }

  // Speak encouragement at milestones
  async speakEncouragement(reps, gameName = 'game') {
    // Check if enabled
    if (!this.isEnabled) {
      console.log('🎤 TTS disabled');
      return false;
    }
    
    // Check cooldown
    const now = Date.now();
    if (now - this.lastSpeakTime < this.cooldownMs) {
      console.log('🎤 Cooldown active, skipping');
      return false;
    }
    
    // Check if already spoken
    const key = `${gameName}_${reps}`;
    if (this.spokenMilestones.has(key)) {
      return false;
    }
    
    // Get message
    const messages = this.messages[reps];
    if (!messages) {
      return false;
    }
    
    const message = messages[Math.floor(Math.random() * messages.length)];
    
    // Mark as spoken and update time
    this.spokenMilestones.set(key, true);
    this.lastSpeakTime = now;
    
    // Speak using ElevenLabs
    console.log(`🎤 Speaking milestone ${reps}: "${message}"`);
    return await this.speakText(message);
  }

  // Reset milestones for a game
  resetMilestones(gameName) {
    const keysToDelete = [];
    for (const key of this.spokenMilestones.keys()) {
      if (key.startsWith(gameName + '_')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.spokenMilestones.delete(key));
    console.log(`🎤 Reset milestones for ${gameName}`);
  }

  // Get status
  getStatus() {
    return {
      service: 'ElevenLabs',
      enabled: this.isEnabled,
      hasApiKey: !!this.apiKey,
      currentVoice: this.currentVoiceName,
      currentVoiceId: this.currentVoiceId,
      voiceSettings: this.voiceSettings,
      availableVoices: Object.keys(this.voiceIds),
      milestonesSpoken: this.spokenMilestones.size,
      browserTTSAvailable: 'speechSynthesis' in window
    };
  }

  // Test different voices
  async testVoices() {
    const testText = "Hello! I'm here to encourage you during your rehabilitation. You're doing great!";
    console.log('🎤 Testing ElevenLabs voices...');
    
    for (const [name, id] of Object.entries(this.voiceIds)) {
      console.log(`\n🎤 Testing voice: ${name}`);
      this.setVoice(name);
      await this.speakText(testText);
      // Wait between voices
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
  }

  // Test with custom settings
  async testWithSettings(text, settings) {
    this.setVoiceSettings(settings);
    return await this.speakText(text);
  }

  // Start inactivity monitoring
  startInactivityMonitoring() {
    // CRITICAL: Always clear existing interval first to prevent duplicates
    if (this.inactivityCheckInterval) {
      console.log('🛑 Clearing existing inactivity interval before starting new one');
      clearInterval(this.inactivityCheckInterval);
      this.inactivityCheckInterval = null;
    }
    
    this.hasAskedIfOkay = false;
    this.lastActivityTime = Date.now();
    
    console.log('🔍 Starting inactivity monitoring (50s threshold)');
    
    this.inactivityCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - this.lastActivityTime;
      
      // Only check if document is visible (not in background tab)
      if (document.hidden) {
        console.log('📱 Document hidden, skipping inactivity check');
        return;
      }
      
      if (timeSinceActivity > this.inactivityThreshold && !this.hasAskedIfOkay) {
        this.hasAskedIfOkay = true;
        const message = this.inactivityMessages[Math.floor(Math.random() * this.inactivityMessages.length)];
        console.log('⏰ Inactivity detected, speaking:', message.substring(0, 50) + '...');
        this.speakText(message);
      }
    }, 1000); // Check every second
  }

  // Stop inactivity monitoring
  stopInactivityMonitoring() {
    if (this.inactivityCheckInterval) {
      console.log('🛑 Stopping inactivity monitoring');
      clearInterval(this.inactivityCheckInterval);
      this.inactivityCheckInterval = null;
    } else {
      console.log('ℹ️ No inactivity monitoring interval to stop');
    }
    this.hasAskedIfOkay = false;
  }

  // CRITICAL: Stop all audio (call this when closing games/pages)
  stopAllAudio() {
    console.log('🛑 ElevenLabs: Stopping all audio playback');
    
    // 1. Stop currently playing HTML5 audio element
    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio = null;
      } catch (error) {
        console.warn('Error stopping current audio:', error);
      }
    }
    
    // 2. Stop all queued audio elements
    while (this.audioQueue.length > 0) {
      const audio = this.audioQueue.shift();
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (error) {
        console.warn('Error stopping queued audio:', error);
      }
    }
    
    // 3. Cancel browser TTS (speechSynthesis)
    if (window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch (error) {
        console.warn('Error canceling speech synthesis:', error);
      }
    }
    
    // 4. Reset all playback state
    this.isPlaying = false;
    this.isSpeaking = false;
    this.currentText = '';
    
    // 5. Stop animations
    this.stopSpeaking();
    
    console.log('✅ ElevenLabs: All audio stopped');
  }

  // Reset activity timer
  resetActivityTimer() {
    this.lastActivityTime = Date.now();
    this.hasAskedIfOkay = false;
  }

  // Update reps and reset activity
  updateActivity(reps, gameName) {
    this.resetActivityTimer();
    this.currentReps = reps;
    
    // Check for milestones
    const messages = this.messages[reps];
    if (messages && this.encouragementMilestones.includes(reps)) {
      this.speakEncouragement(reps, gameName);
    }
  }
}

// Create global instance
const elevenLabsTTS = new ElevenLabsTTSService();

// Expose globally
window.elevenLabsTTS = elevenLabsTTS;

// Test functions
window.testElevenLabsTTS = async () => {
  console.log('🎤 Testing ElevenLabs TTS...');
  const success = await elevenLabsTTS.speakText('Testing ElevenLabs text to speech. Great job! Keep going!');
  console.log(success ? '✅ ElevenLabs TTS test successful' : '❌ ElevenLabs TTS test failed');
  return success;
};

window.testElevenLabsVoices = async () => {
  console.log('🎤 Testing all ElevenLabs voices...');
  await elevenLabsTTS.testVoices();
};

window.testVoiceEncouragementEL = async () => {
  console.log('🎤 Testing encouragement with ElevenLabs...');
  elevenLabsTTS.resetMilestones('test');
  
  // Test different milestone messages
  for (const reps of [5, 10, 15, 20, 25, 30]) {
    console.log(`\n🎏 Milestone ${reps} reps:`);
    await elevenLabsTTS.speakEncouragement(reps, 'test');
    await new Promise(resolve => setTimeout(resolve, 3000));
  }
};

window.testBrowserTTSEL = () => {
  console.log('🔊 Testing browser TTS fallback...');
  elevenLabsTTS.fallbackToBrowserTTS('Testing browser text to speech fallback.');
};

window.testAnimatedSubtitles = async () => {
  console.log('🎬 Testing animated subtitles LEFT of Lottie...');
  console.log('📍 Subtitles should appear LEFT of Mendy animation with LEFT-TO-RIGHT slide!');
  
  // Debug CSS classes
  setTimeout(() => {
    const subtitleEl = document.querySelector('.animated-subtitles');
    if (subtitleEl) {
      console.log('🔍 Subtitle element found:', subtitleEl);
      console.log('🔍 Applied classes:', subtitleEl.className);
      console.log('🔍 Computed styles:', {
        position: getComputedStyle(subtitleEl).position,
        left: getComputedStyle(subtitleEl).left,
        bottom: getComputedStyle(subtitleEl).bottom,
        transform: getComputedStyle(subtitleEl).transform
      });
    } else {
      console.log('❌ No subtitle element found!');
    }
  }, 1000);
  
  const testMessages = [
    "Hello! I'm Mendy, your encouraging fitness coach. Let's get moving together!",
    "Testing subtitle positioning LEFT of Lottie animation!",
    "This should appear on the LEFT side and slide in from left!"
  ];
  
  for (const message of testMessages) {
    console.log(`🎬 Playing: ${message.substring(0, 50)}...`);
    console.log('🎭 Watch: Mendy should glow and subtitles slide in from LEFT');
    await elevenLabsTTS.speakText(message);
    await new Promise(resolve => setTimeout(resolve, 4000)); // Wait between messages
  }
  
  console.log('🎬 Animation test complete!');
};

// Auto-enable on load
console.log('🎤 ElevenLabs TTS Service loaded and ready!');
console.log('🎤 Default voice:', elevenLabsTTS.currentVoiceName);
console.log('🎤 Available voices:', Object.keys(elevenLabsTTS.voiceIds).join(', '));

export default elevenLabsTTS;
